import { Avatar, Group, Stack, Text, ThemeIcon, UnstyledButton } from '@mantine/core';
import { IconCrown } from '@tabler/icons-react';
import { useRouterState } from '@tanstack/react-router';
import { isAdmin } from '~api';
import { useProfile } from '~portal/api';
import { AccountMenu, useAccountIdentity } from '~portal/components/AccountMenu';
import { ACCOUNT, PLANS } from '~portal/data/account';
import { Cmp } from '~portal/lib';
import * as styles from './GlobalNav.css';

/** @desc The plan the account fixture is on — see {@link AccountBlock} for why not `users.me`. */
const plan = PLANS.find((candidate) => candidate.id === ACCOUNT.currentPlanId);

/**
 * @desc The platform's account block, pinned to the foot of the global nav and of the chat
 * sidebar (Figma `426:7806`, `14:1450`): an initials avatar (never a photo — client decision),
 * the name, the plan underneath, and a crown for an administrator. Clicking it opens the shared
 * `AccountMenu` dropdown (Account, Sign out).
 *
 * The plan line is the account fixture's current plan (`ACCOUNT.currentPlanId` in `PLANS`), so it
 * says what the /account page says. `users.me` carries no plan yet — the platform roles
 * (`platform-basic`/`platform-premium`) will be its source once tiering is decided (D-14) — so the
 * plan is read straight from `data/account` rather than through `~portal/data`, whose barrel
 * would pull every fixture hook into the shell. The standalone build keeps `AccountMenu`'s own
 * block, whose second line is the organisation name.
 *
 * On `/account` the block is the current destination and takes the nav pills' selected tint
 * (Figma G7, `295:53567`: Primary 20), via the same `data-selected` the pills use.
 */
export const AccountBlock: Cmp = function () {
  const { name } = useAccountIdentity();
  const profile = useProfile();
  const path = useRouterState({ select: (state) => state.location.pathname });

  const admin = isAdmin(profile);
  const selected = path === '/account' || path.startsWith('/account/');

  return (
    <AccountMenu
      placement="sidebar"
      target={
        <UnstyledButton
          aria-label="Account menu"
          className={styles.accountButton}
          data-selected={selected || undefined}
          aria-current={selected ? 'page' : undefined}
        >
          <Group gap="sm" wrap="nowrap" p="xs">
            <Avatar name={name} color="initials" size={36} radius="xl" />
            <Stack gap={0} miw={0} flex={1}>
              <Text size="sm" fw={500} lh={1.3} className={styles.line}>
                {name}
              </Text>
              {plan && (
                <Text size="xs" c="dimmed" lh={1.3} className={styles.line}>
                  {plan.name} Plan
                </Text>
              )}
            </Stack>
            {admin && (
              <ThemeIcon
                variant="light"
                color="yellow"
                size={28}
                radius="md"
                className={styles.crown}
                aria-label="Administrator"
                role="img"
              >
                <IconCrown size={18} />
              </ThemeIcon>
            )}
          </Group>
        </UnstyledButton>
      }
    />
  );
};
